import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { getIssues, Issue } from '../../api/issues';
import { CockpitEnvironment, SearchHit, searchEverywhere } from '../data/cockpit';
import { envKind, envSummary, envTone, useCockpit } from '../data/CockpitContext';
import { apiError } from '../data/usePoll';
import { EnvChip, Icon, Kv, Lamp, Loading } from '../components/ui';
import { objectName } from '../pages/issueModel';
import { v2 } from '../paths';
import { SECTION_LABEL, useScope } from './scope';

type Mode = 'search' | 'switch';

/** Turns a backlog issue into a hit, for an API without /Cockpit/search yet. */
const fromIssue = (i: Issue): SearchHit => ({
    customer: i.customer,
    environment: i.environment,
    kind: i.environment.toLowerCase().startsWith('prod') ? 'prod' : 'test',
    objectType: i.objectType,
    relationId: i.relationId,
    label: i.invoiceId || i.relationId,
    issueKey: i.key,
    issueTitle: i.title,
    status: i.status,
    owner: i.owner ?? null,
});

/** Ctrl+K: find an invoice, payment or customer anywhere, or jump to another environment. */
export const SearchPalette = ({ mode, onMode, onClose }: { mode: Mode; onMode: (m: Mode) => void; onClose: () => void }) => {
    const axios = useAxiosSwagger();
    const navigate = useNavigate();
    const location = useLocation();
    const scope = useScope();
    const { environments, customers } = useCockpit();
    const [q, setQ] = useState('');
    const [hits, setHits] = useState<SearchHit[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();
    const [sel, setSel] = useState(0);
    const input = useRef<HTMLInputElement>(null);
    const list = useRef<HTMLDivElement>(null);

    useEffect(() => {
        input.current?.focus();
        setSel(0);
    }, [mode]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Tab') {
                e.preventDefault();
                onMode(mode === 'search' ? 'switch' : 'search');
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mode, onClose, onMode]);

    useEffect(() => {
        if (mode !== 'search' || !axios || q.trim().length < 3) {
            setHits([]);
            setBusy(false);
            return;
        }

        const controller = new AbortController();
        setBusy(true);
        const timer = setTimeout(async () => {
            setError(undefined);
            try {
                setHits(await searchEverywhere(axios, q.trim(), controller.signal));
            } catch (e: any) {
                if (controller.signal.aborted) return;
                if (e?.response?.status === 404) {
                    // An API without /Cockpit/search: look through the backlog instead.
                    const found = await getIssues(axios, { q: q.trim(), status: 'all' }, controller.signal).catch(() => null);
                    setHits(found ? found.items.slice(0, 20).map(fromIssue) : []);
                } else setError(apiError(e));
            } finally {
                if (!controller.signal.aborted) setBusy(false);
            }
        }, 300);

        return () => {
            controller.abort();
            clearTimeout(timer);
        };
    }, [axios, q, mode]);

    const tree = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return customers
            .filter((c) => !needle || c.toLowerCase().includes(needle))
            .map((c) => ({ customer: c, envs: environments.filter((e) => e.customer === c) }));
    }, [customers, environments, q]);

    // The environments in the order they are shown, for the arrow keys.
    const flat = useMemo(() => tree.flatMap((c) => c.envs), [tree]);

    // A hit opens its dossier: what happened to it, whose it is and where it stands. The issue is one click further.
    const openHit = (h: SearchHit) => {
        onClose();
        const label = scope.objectId
            ? scope.objectId
            : scope.inEnv
              ? `${SECTION_LABEL[scope.section].toLowerCase()} ${scope.customer} ${scope.environment}`
              : SECTION_LABEL[scope.section].toLowerCase();
        navigate(v2.object(h.customer, h.environment, h.relationId), { state: { from: location.pathname + location.search, label } });
    };

    const openIssue = (h: SearchHit) => {
        onClose();
        if (h.issueKey) navigate(v2.issue(h.customer, h.environment, h.issueKey));
    };

    const openEnv = (e: CockpitEnvironment) => {
        onClose();
        navigate(v2.env(e.customer, e.environment));
    };

    const count = mode === 'search' ? hits.length : flat.length;

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (count === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSel((s) => Math.min(count - 1, s + 1));
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSel((s) => Math.max(0, s - 1));
        }
        if (e.key === 'Enter') {
            if (mode === 'search') openHit(hits[sel]);
            else if (flat[sel]) openEnv(flat[sel]);
        }
    };

    useEffect(() => {
        list.current?.querySelector('.sel')?.scrollIntoView({ block: 'nearest' });
    }, [sel]);

    const current = hits[sel];
    const here = (e: CockpitEnvironment) =>
        e.customer.toLowerCase() === (scope.customer ?? '').toLowerCase() && e.environment.toLowerCase() === (scope.environment ?? '').toLowerCase();

    return (
        <>
            <button className="scrim dialog-scrim" aria-label="Sluiten" onClick={onClose} />
            <div className="dialog palette" role="dialog" aria-label="Zoeken">
                <div className="pal-head">
                    <div className="pal-input">
                        <Icon name="search" size={18} />
                        <input
                            ref={input}
                            aria-label={mode === 'search' ? 'Zoek een object' : 'Zoek een klant'}
                            value={q}
                            onChange={(e) => {
                                setQ(e.target.value);
                                setSel(0);
                            }}
                            onKeyDown={onKeyDown}
                            placeholder={mode === 'search' ? 'Factuurnummer, betaling of klant-id…' : 'Klantnaam…'}
                        />
                        {busy && <span className="spin" />}
                    </div>
                    <div className="pal-bar">
                        <div className="seg" role="tablist" aria-label="Zoek naar">
                            <button role="tab" aria-selected={mode === 'search'} className={mode === 'search' ? 'on' : ''} onClick={() => onMode('search')}>
                                Objecten
                            </button>
                            <button role="tab" aria-selected={mode === 'switch'} className={mode === 'switch' ? 'on' : ''} onClick={() => onMode('switch')}>
                                Omgevingen
                            </button>
                        </div>
                        <button className="kbd" onClick={onClose}>Esc</button>
                    </div>
                </div>

                {mode === 'search' && (
                    <div className="pal-split">
                        <div className="pal-list" ref={list}>
                            {q.trim().length < 3 && (
                                <div className="empty">
                                    Typ minstens drie tekens.
                                    <div className="hrow" style={{ gap: 6, marginTop: 10 }}>
                                        <span className="chip">INV-10231</span>
                                        <span className="chip">een betaling-id</span>
                                        <span className="chip">een Chargebee-klant</span>
                                    </div>
                                </div>
                            )}
                            {error && <div className="empty">{error}</div>}
                            {q.trim().length >= 3 && busy && hits.length === 0 && <Loading>Zoeken in alle omgevingen…</Loading>}
                            {q.trim().length >= 3 && !busy && hits.length === 0 && !error && <div className="empty">Niets gevonden voor “{q.trim()}”.</div>}
                            {hits.map((h, i) => (
                                <button
                                    key={`${h.customer}/${h.environment}/${h.objectType}/${h.relationId}/${i}`}
                                    className={`pal-row ${i === sel ? 'sel' : ''}`}
                                    onMouseEnter={() => setSel(i)}
                                    onClick={() => openHit(h)}
                                >
                                    <span className="mono t">{h.label}</span>
                                    <span className="m">
                                        <EnvChip kind={h.kind} environment={h.environment} />
                                        <span>{h.customer}</span>
                                        <span>{objectName(h.objectType)}</span>
                                    </span>
                                    {h.issueKey ? <span className="chip warn">issue</span> : <span className="chip">object</span>}
                                </button>
                            ))}
                        </div>
                        <div className="pal-preview">
                            {current ? (
                                <>
                                    <div className="hrow" style={{ gap: 8 }}>
                                        <EnvChip kind={current.kind} environment={current.environment} />
                                        <span className="mut">{current.customer}</span>
                                    </div>
                                    <div className="mono" style={{ fontSize: 20, fontWeight: 600 }}>{current.label}</div>
                                    <Kv
                                        rows={[
                                            ['Soort', objectName(current.objectType)],
                                            ['Id', <span className="mono">{current.relationId}</span>],
                                            ['Issue', current.issueTitle ?? 'geen'],
                                            ['Stand', current.status ?? '–'],
                                        ]}
                                    />
                                    <div className="hrow">
                                        <button className="btn pri" onClick={() => openHit(current)}>Dossier en timeline</button>
                                        {current.issueKey && (
                                            <button className="btn" onClick={() => openIssue(current)}>Issue openen</button>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="mut">Kies links een resultaat.</div>
                            )}
                        </div>
                    </div>
                )}

                {mode === 'switch' && (
                    <div className="pal-list tall" ref={list}>
                        {tree.length === 0 && <div className="empty">Geen klant met “{q.trim()}”.</div>}
                        {tree.map((c) => (
                            <div key={c.customer} className="pal-cust">
                                <b>{c.customer}</b>
                                <div className="pal-envs">
                                    {c.envs.map((e) => {
                                        const i = flat.indexOf(e);
                                        return (
                                            <button
                                                key={e.environment}
                                                className={`pal-env ${envKind(e)} ${i === sel ? 'sel' : ''} ${here(e) ? 'here' : ''}`}
                                                onMouseEnter={() => setSel(i)}
                                                onClick={() => openEnv(e)}
                                            >
                                                <span className="n">{e.environment.toUpperCase()}</span>
                                                <span className="s">
                                                    <Lamp tone={envTone(e)} size={7} />
                                                    {envSummary(e)}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="pal-foot">
                    <span><span className="kbd">↑</span> <span className="kbd">↓</span> kiezen</span>
                    <span><span className="kbd">↵</span> openen</span>
                    <span><span className="kbd">Tab</span> objecten / omgevingen</span>
                    <span><span className="kbd">Esc</span> sluiten</span>
                </div>
            </div>
        </>
    );
};
